/**
 * Enhanced AdminClassStudentAddPage using the new component system
 * Demonstrates advanced student enrollment with filtering, batch operations, and better UX
 */

import React, { useEffect, useState } from 'react';
import {
  FiCheck,
  FiClock,
  FiSearch,
  FiUserPlus,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

// New component system imports
import {
  Avatar,
  Badge,
  Checkbox,
  Divider,
  HStack,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  Card,
  Button as CustomButton,
  DataTable,
  FormField,
  FormPageLayout,
  StatsCard,
  TableColumn,
} from '@grcs/components';

// Existing hooks and types
import { useData } from '@grcs/context/DataContext';
import {
  useClassOperations,
  useStudentOperations,
  useUserOperations,
} from '@grcs/hook/useDatabase';
import { UserProfile, UserRole } from '@grcs/types/UserRole';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface StudentEnrollmentStats {
  totalAvailableStudents: number;
  enrolledStudents: number;
  pendingEnrollments: number;
  className: string;
  semesterYear: string;
}

interface EnhancedUserProfile extends UserProfile {
  enrollment_status?: 'available' | 'enrolled' | 'pending' | 'in_other_class';
  current_class_name?: string;
  last_enrollment?: string;
}

export const AdminClassStudentAddPageEnhanced: React.FC = () => {
  const { supabase } = useData();
  const classOps = useClassOperations();
  const userOps = useUserOperations();
  const studentOps = useStudentOperations();
  const { year, classId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [users, setUsers] = useState<EnhancedUserProfile[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [stats, setStats] = useState<StudentEnrollmentStats>({
    totalAvailableStudents: 0,
    enrolledStudents: 0,
    pendingEnrollments: 0,
    className: '',
    semesterYear: '',
  });

  // Load class information and students
  useEffect(() => {
    const loadClassData = async () => {
      if (!classId) return;

      try {
        setIsLoading(true);

        // Get class info
        const { data: classData } = await classOps.getClassInfoByClassId(
          classId,
        );
        if (classData && classData.length > 0) {
          const classInfo = classData[0];
          setStats((prev) => ({
            ...prev,
            className: classInfo.class_name,
            semesterYear: classInfo.semester_year,
          }));
        }

        // Get current students in this class
        const { data: enrolledStudents } =
          await studentOps.getStudentsByClassId(classId);
        const enrolledUserTokens = new Set(
          (enrolledStudents || []).map((s) => s.student_token),
        );

        // Get all students
        const { data: allUsers } = await userOps.getUsersByRole(
          UserRole.STUDENT,
        );
        if (allUsers) {
          // Get students in other classes for status checking
          const { data: allStudentEnrollments } = await supabase
            .from(TABLE_NAMES.STUDENT)
            .select('student_token, class_token, class_name, deleted')
            .eq('deleted', false);

          const enrollmentMap = new Map();
          (allStudentEnrollments || []).forEach((enrollment) => {
            if (!enrollmentMap.has(enrollment.student_token)) {
              enrollmentMap.set(enrollment.student_token, []);
            }
            enrollmentMap.get(enrollment.student_token).push(enrollment);
          });

          const enhancedUsers: EnhancedUserProfile[] = allUsers.map((user) => {
            let enrollmentStatus: EnhancedUserProfile['enrollment_status'] =
              'available';
            let currentClassName = '';

            const userEnrollments = enrollmentMap.get(user.user_token) || [];

            if (enrolledUserTokens.has(user.user_token)) {
              enrollmentStatus = 'enrolled';
              currentClassName = stats.className;
            } else if (userEnrollments.length > 0) {
              enrollmentStatus = 'in_other_class';
              currentClassName = userEnrollments[0].class_name;
            }

            return {
              ...user,
              enrollment_status: enrollmentStatus,
              current_class_name: currentClassName,
              last_enrollment: new Date().toISOString(), // Mock data - in real app, get from activity logs
            };
          });

          setUsers(enhancedUsers);

          // Update stats
          const available = enhancedUsers.filter(
            (u) => u.enrollment_status === 'available',
          ).length;
          const enrolled = enhancedUsers.filter(
            (u) => u.enrollment_status === 'enrolled',
          ).length;

          setStats((prev) => ({
            ...prev,
            totalAvailableStudents: available,
            enrolledStudents: enrolled,
            pendingEnrollments: selectedUsers.size,
          }));
        }
      } catch (error: any) {
        console.error('Error loading class data:', error);
        toast({
          title: 'Error Loading Data',
          description:
            error.message || 'Failed to load class and student information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadClassData();
  }, [
    classId,
    classOps,
    studentOps,
    userOps,
    supabase,
    stats.className,
    toast,
    selectedUsers.size,
  ]);

  // Update pending enrollments count when selection changes
  useEffect(() => {
    setStats((prev) => ({
      ...prev,
      pendingEnrollments: selectedUsers.size,
    }));
  }, [selectedUsers.size]);

  // Filter users based on search and tab
  const filteredUsers = users.filter((user) => {
    if (!user) return false;

    const matchesSearch =
      searchTerm === '' ||
      `${user.first_name || ''} ${user.last_name || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    switch (activeTab) {
      case 0: // All students
        return matchesSearch;
      case 1: // Available only
        return matchesSearch && user.enrollment_status === 'available';
      case 2: // In other classes
        return matchesSearch && user.enrollment_status === 'in_other_class';
      case 3: // Already enrolled
        return matchesSearch && user.enrollment_status === 'enrolled';
      default:
        return matchesSearch;
    }
  });

  // Pagination calculations
  const totalItems = filteredUsers.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Reset to page 1 when search or tab changes
  const resetPagination = () => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  };

  // Reset pagination when filters change
  React.useEffect(() => {
    resetPagination();
  }, [searchTerm, activeTab, totalPages]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate visible data for current page
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleUsers = filteredUsers.slice(startIndex, endIndex);

  // Pagination configuration
  const paginationConfig = {
    currentPage,
    totalPages,
    onPageChange: handlePageChange,
    pageSize,
    totalItems,
    onPageSizeChange: handlePageSizeChange,
    showPageSizeSelector: true,
  };

  // Event handlers
  const handleUserSelect = (userToken: string) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userToken)) {
      newSelection.delete(userToken);
    } else {
      newSelection.add(userToken);
    }
    setSelectedUsers(newSelection);
  };

  const handleSelectAll = () => {
    const availableUsersOnPage = visibleUsers.filter(
      (u) => u?.enrollment_status === 'available' && u?.user_token,
    );
    const availableTokensOnPage = availableUsersOnPage
      .map((u) => u.user_token)
      .filter(Boolean);

    // Check if all available users on current page are selected
    const allPageUsersSelected = availableTokensOnPage.every((token) => {
      if (!token) return false;
      return selectedUsers.has(token);
    });

    if (allPageUsersSelected && availableTokensOnPage.length > 0) {
      // Deselect all users on current page
      const newSelection = new Set(selectedUsers);
      availableTokensOnPage.forEach((token) => {
        if (!token) return;
        newSelection.delete(token);
      });
      setSelectedUsers(newSelection);
    } else {
      // Select all available users on current page
      const newSelection = new Set(selectedUsers);
      availableTokensOnPage.forEach((token) => {
        if (!token) return;
        newSelection.delete(token);
      });
      setSelectedUsers(newSelection);
    }
  };

  const handleBulkEnroll = async () => {
    if (selectedUsers.size === 0) {
      toast({
        title: 'No Students Selected',
        description: 'Please select at least one student to enroll',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsEnrolling(true);

    try {
      const enrollmentPromises = Array.from(selectedUsers).map(
        async (userToken) => {
          return await handleSingleEnrollment(userToken);
        },
      );

      const results = await Promise.allSettled(enrollmentPromises);
      const successful = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      if (successful > 0) {
        toast({
          title: 'Enrollment Complete',
          description: `Successfully enrolled ${successful} student${
            successful > 1 ? 's' : ''
          }${failed > 0 ? `. ${failed} failed.` : ''}`,
          status: successful === selectedUsers.size ? 'success' : 'warning',
          duration: 5000,
          isClosable: true,
        });

        // Navigate back to class students page
        navigate(`/admin/${year}/class/${classId}/students`);
      }
    } catch (error: any) {
      console.error('Error during bulk enrollment:', error);
      toast({
        title: 'Enrollment Error',
        description: error.message || 'Failed to enroll students',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleSingleEnrollment = async (userToken: string) => {
    // Check if student already exists in this class
    const { data: existing } = await supabase
      .from(TABLE_NAMES.STUDENT)
      .select('*')
      .eq('student_token', userToken)
      .eq('class_token', classId);

    if (existing && existing.length > 0) {
      // Reactivate if soft-deleted
      await studentOps.updateDataById(
        TABLE_NAMES.STUDENT,
        'student_token',
        userToken,
        { deleted: false },
      );
      return;
    }

    // Check if student exists in other classes
    const { data: otherEnrollments } = await supabase
      .from(TABLE_NAMES.STUDENT)
      .select('*')
      .eq('student_token', userToken);

    if (otherEnrollments && otherEnrollments.length > 0) {
      // Update existing enrollment to new class
      const [student] = otherEnrollments;
      await studentOps.updateDataById(
        TABLE_NAMES.STUDENT,
        'student_token',
        userToken,
        {
          ...student,
          semester_year: stats.semesterYear,
          class_token: classId,
          class_name: stats.className,
          deleted: false,
        },
      );
      return;
    }

    // Create new enrollment
    const { data: userData } = await userOps.getDataById(
      TABLE_NAMES.USER,
      'user_token',
      userToken,
    );

    const { error } = await studentOps.insertData(TABLE_NAMES.STUDENT, {
      student_token: userToken,
      class_token: classId,
      class_name: stats.className,
      first_name: userData?.[0]?.first_name,
      last_name: userData?.[0]?.last_name,
      email: userData?.[0]?.email,
    });

    if (error) {
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(`/admin/${year}/class/${classId}/students`);
  };

  // Table columns
  const columns: TableColumn<EnhancedUserProfile>[] = [
    {
      key: 'select',
      header: (
        <Checkbox
          isChecked={
            selectedUsers.size > 0 &&
            selectedUsers.size ===
              filteredUsers.filter(
                (u) => u?.enrollment_status === 'available' && u?.user_token,
              ).length
          }
          isIndeterminate={
            selectedUsers.size > 0 &&
            selectedUsers.size <
              filteredUsers.filter(
                (u) => u?.enrollment_status === 'available' && u?.user_token,
              ).length
          }
          onChange={handleSelectAll}
        />
      ),
      render: (user: EnhancedUserProfile) => (
        <Checkbox
          isChecked={
            user?.user_token ? selectedUsers.has(user.user_token) : false
          }
          onChange={() => user?.user_token && handleUserSelect(user.user_token)}
          isDisabled={!user || user.enrollment_status !== 'available'}
        />
      ),
    },
    {
      key: 'first_name',
      header: 'Student',
      render: (user: EnhancedUserProfile, value: string) => {
        if (!user) return <div>-</div>;

        return (
          <HStack spacing={3}>
            <Avatar
              size="sm"
              name={`${user.first_name || ''} ${user.last_name || ''}`}
              bg={
                user.enrollment_status === 'available'
                  ? 'blue.500'
                  : user.enrollment_status === 'enrolled'
                  ? 'green.500'
                  : 'gray.500'
              }
            />
            <div>
              <div style={{ fontWeight: 600 }}>
                {user.first_name || ''} {user.last_name || ''}
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                {user.email || ''}
              </div>
            </div>
          </HStack>
        );
      },
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (user: EnhancedUserProfile, value: string) => (
        <Text fontSize="sm">{value || user?.phone || 'N/A'}</Text>
      ),
    },
    {
      key: 'enrollment_status',
      header: 'Status',
      render: (user: EnhancedUserProfile, value: string) => {
        if (!user) return <div>-</div>;

        const statusConfig = {
          available: { color: 'green', label: 'Available' },
          enrolled: { color: 'blue', label: 'Already Enrolled' },
          in_other_class: { color: 'orange', label: 'In Other Class' },
          pending: { color: 'yellow', label: 'Pending' },
        };

        const config =
          statusConfig[value as keyof typeof statusConfig] ||
          statusConfig.available;

        return (
          <VStack spacing={1} align="start">
            <Badge colorScheme={config.color} size="sm">
              {config.label}
            </Badge>
            {user.current_class_name &&
              user.enrollment_status !== 'available' && (
                <Text fontSize="xs" color="gray.600">
                  {user.current_class_name}
                </Text>
              )}
          </VStack>
        );
      },
    },
    {
      key: 'user_token',
      header: 'Actions',
      render: (user: EnhancedUserProfile, value: string) => {
        if (!user || !user.user_token) return <div>-</div>;

        return (
          <HStack spacing={2}>
            {user.enrollment_status === 'available' && (
              <CustomButton
                size="sm"
                variant="primary"
                onClick={() => {
                  if (user.user_token) {
                    setSelectedUsers(new Set([user.user_token]));
                  }
                }}
                disabled={isEnrolling}
              >
                <FiUserPlus size={14} />
                Enroll
              </CustomButton>
            )}
            {user.enrollment_status === 'enrolled' && (
              <Badge colorScheme="green" size="sm">
                <FiCheck size={12} /> Enrolled
              </Badge>
            )}
            {user.enrollment_status == 'in_other_class' && (
              <CustomButton
                size="sm"
                variant="warning"
                onClick={() => {
                  if (user.user_token) {
                    setSelectedUsers(new Set([user.user_token]));
                  }
                }}
                disabled={isEnrolling}
              >
                <FiUserPlus size={14} />
                Switch class
              </CustomButton>
            )}
          </HStack>
        );
      },
    },
  ];

  return (
    <FormPageLayout
      title="Add Students"
      subtitle={`Enroll students in ${stats.className}`}
      breadcrumb={{
        items: [
          { label: 'Admin', href: '/admin' },
          { label: 'Classes', href: `/admin/${year}/class` },
          {
            label: stats.className,
            href: `/admin/${year}/class/${classId}/students`,
          },
          { label: 'Add Students', isCurrent: true },
        ],
      }}
      onCancel={handleCancel}
      maxWidth="1200px"
      actions={
        <HStack spacing={3}>
          <CustomButton variant="secondary" onClick={handleCancel}>
            <FiX size={16} />
            Cancel
          </CustomButton>
          <CustomButton
            variant="primary"
            onClick={handleBulkEnroll}
            disabled={selectedUsers.size === 0 || isEnrolling}
            loading={isEnrolling}
          >
            <FiUserPlus size={16} />
            Enroll Selected ({selectedUsers.size})
          </CustomButton>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <StatsCard
            title="Available Students"
            value={stats.totalAvailableStudents}
            icon={FiUsers}
            variant="success"
            subtitle="Available"
          />

          <StatsCard
            title="Already Enrolled"
            value={stats.enrolledStudents}
            icon={FiCheck}
            variant="info"
            subtitle="Enrolled"
          />

          <StatsCard
            title="Selected"
            value={stats.pendingEnrollments}
            icon={FiClock}
            variant="warning"
            subtitle="Selected"
          />

          <StatsCard
            title="Total Students"
            value={users.filter(Boolean).length}
            icon={FiUsers}
            variant="secondary"
            subtitle="Total"
          />
        </SimpleGrid>

        {/* Search and Filter */}
        <Card padding={6}>
          <VStack spacing={4} align="stretch">
            <FormField
              label="Search"
              name="search"
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={setSearchTerm}
              icon={<FiSearch />}
            />

            <Divider />

            {/* Bulk Actions */}
            {selectedUsers.size > 0 && (
              <Card padding={4} variant="filled">
                <HStack justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="600">
                    {selectedUsers.size} student
                    {selectedUsers.size > 1 ? 's' : ''} selected
                  </Text>
                  <HStack spacing={2}>
                    <CustomButton
                      size="sm"
                      variant="secondary"
                      onClick={() => setSelectedUsers(new Set())}
                    >
                      Clear Selection
                    </CustomButton>
                    <CustomButton
                      size="sm"
                      variant="primary"
                      onClick={handleBulkEnroll}
                      loading={isEnrolling}
                    >
                      <FiUserPlus size={14} />
                      Enroll Selected
                    </CustomButton>
                  </HStack>
                </HStack>
              </Card>
            )}
          </VStack>
        </Card>

        {/* Student List with Tabs */}
        <Card padding={6}>
          <VStack spacing={6} align="stretch">
            <Text fontSize="xl" fontWeight="600">
              Students
            </Text>

            <Tabs index={activeTab} onChange={setActiveTab}>
              <TabList>
                <Tab>All ({users.filter(Boolean).length})</Tab>
                <Tab>
                  Available (
                  {
                    users.filter((u) => u?.enrollment_status === 'available')
                      .length
                  }
                  )
                </Tab>
                <Tab>
                  In Other Classes (
                  {
                    users.filter(
                      (u) => u?.enrollment_status === 'in_other_class',
                    ).length
                  }
                  )
                </Tab>
                <Tab>
                  Already Enrolled (
                  {
                    users.filter((u) => u?.enrollment_status === 'enrolled')
                      .length
                  }
                  )
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <DataTable
                    data={filteredUsers}
                    columns={columns}
                    loading={isLoading}
                    pagination={paginationConfig}
                    emptyState={
                      <VStack spacing={4} py={12}>
                        <FiUsers size={48} opacity={0.4} />
                        <div style={{ textAlign: 'center' }}>
                          <p
                            style={{
                              fontSize: '1.125rem',
                              fontWeight: 600,
                              marginBottom: '0.5rem',
                            }}
                          >
                            No students
                          </p>
                          <p style={{ opacity: 0.8 }}>
                            {searchTerm
                              ? 'Try adjusting your search terms'
                              : 'No students available in the system'}
                          </p>
                        </div>
                      </VStack>
                    }
                  />
                </TabPanel>
                <TabPanel px={0}>
                  <DataTable
                    data={filteredUsers}
                    columns={columns}
                    loading={isLoading}
                    pagination={paginationConfig}
                    emptyState={
                      <VStack spacing={4} py={12}>
                        <FiUserPlus size={48} opacity={0.4} />
                        <div style={{ textAlign: 'center' }}>
                          <p
                            style={{
                              fontSize: '1.125rem',
                              fontWeight: 600,
                              marginBottom: '0.5rem',
                            }}
                          >
                            None available
                          </p>
                          <p style={{ opacity: 0.8 }}>
                            All students are already enrolled in classes
                          </p>
                        </div>
                      </VStack>
                    }
                  />
                </TabPanel>
                <TabPanel px={0}>
                  <DataTable
                    data={filteredUsers}
                    columns={columns}
                    loading={isLoading}
                    pagination={paginationConfig}
                    emptyState={
                      <VStack spacing={4} py={12}>
                        <FiUsers size={48} opacity={0.4} />
                        <div style={{ textAlign: 'center' }}>
                          <p
                            style={{
                              fontSize: '1.125rem',
                              fontWeight: 600,
                              marginBottom: '0.5rem',
                            }}
                          >
                            None in other classes
                          </p>
                          <p style={{ opacity: 0.8 }}>
                            Students can be transferred between classes if
                            needed
                          </p>
                        </div>
                      </VStack>
                    }
                  />
                </TabPanel>
                <TabPanel px={0}>
                  <DataTable
                    data={filteredUsers}
                    columns={columns}
                    loading={isLoading}
                    pagination={paginationConfig}
                    emptyState={
                      <VStack spacing={4} py={12}>
                        <FiCheck size={48} opacity={0.4} />
                        <div style={{ textAlign: 'center' }}>
                          <p
                            style={{
                              fontSize: '1.125rem',
                              fontWeight: 600,
                              marginBottom: '0.5rem',
                            }}
                          >
                            None enrolled
                          </p>
                          <p style={{ opacity: 0.8 }}>
                            Switch to "Available" tab to enroll students
                          </p>
                        </div>
                      </VStack>
                    }
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </Card>
      </VStack>
    </FormPageLayout>
  );
};
